import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Coins, ChevronDown, ChevronUp } from "lucide-react";
import { sharedApi } from "@/lib/api";
import StoreScopeTag from "@/components/partner/StoreScopeTag";

/**
 * Iter-600 §5 — vendor coin insights: 8-week table of coins redeemed at
 * this store (value, codes used/issued) + totals.
 *   GET /vendors/me/coin-insights (vendor JWT, X-App-Platform)
 */
const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

export default function VendorCoinInsightsCard() {
  const [open, setOpen] = useState(true);

  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/classifieds/engagement/vendors/me", OPTS)).data,
  });

  const insightsQ = useQuery({
    queryKey: ["vendor-coin-insights"],
    enabled: !!meQ.data?.id,
    retry: 0,
    staleTime: 60_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/coin-insights", OPTS)).data,
  });

  if (meQ.isLoading || meQ.isError || insightsQ.isError) return null;

  const weeks = insightsQ.data?.weeks || insightsQ.data?.rows || [];
  const totals = insightsQ.data?.totals || null;

  return (
    <section data-testid="vendor-coin-insights-card" className="mt-6 rounded-2xl bg-white shadow-card p-6 md:p-8">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="vendor-coin-insights-toggle"
        className="w-full flex items-center gap-2 flex-wrap text-left"
      >
        <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 grid place-items-center shrink-0">
          <Coins className="w-4 h-4" />
        </span>
        <h2 className="font-display text-2xl font-black text-slate-900">Coin insights</h2>
        <StoreScopeTag />
        <span className="ml-auto text-slate-400">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      <p className="mt-1 text-sm text-slate-500">Reward coins redeemed at your store over the last 8 weeks.</p>

      {open && (
        insightsQ.isLoading ? (
          <div className="mt-4 text-xs text-slate-400">Loading insights…</div>
        ) : weeks.length === 0 ? (
          <div className="mt-4 text-sm text-slate-500" data-testid="vendor-coin-insights-empty">
            No coin activity yet — redemptions will show up here week by week.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="vendor-coin-insights-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3 font-bold">Week</th>
                  <th className="py-2 pr-3 font-bold text-right">Coins redeemed</th>
                  <th className="py-2 pr-3 font-bold text-right">Value</th>
                  <th className="py-2 pr-3 font-bold text-right">Codes used</th>
                  <th className="py-2 font-bold text-right">Codes issued</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w, i) => (
                  <tr key={w.label || i} className="border-b border-slate-50 text-slate-700">
                    <td className="py-2 pr-3 font-semibold">{w.label}</td>
                    <td className="py-2 pr-3 text-right">{w.coins_redeemed ?? 0}</td>
                    <td className="py-2 pr-3 text-right">${Number(w.value_nzd ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right">{w.codes_used ?? 0}</td>
                    <td className="py-2 text-right">{w.codes_issued ?? 0}</td>
                  </tr>
                ))}
                {totals && (
                  <tr className="font-black text-slate-900" data-testid="vendor-coin-insights-totals">
                    <td className="py-2 pr-3">Total</td>
                    <td className="py-2 pr-3 text-right">{totals.coins_redeemed ?? 0}</td>
                    <td className="py-2 pr-3 text-right">${Number(totals.value_nzd ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right">{totals.codes_used ?? 0}</td>
                    <td className="py-2 text-right">{totals.codes_issued ?? 0}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}
